import {
	animatePages,
	apiUrl,
	cookiesActivated,
	history,
	isRetina,
	navigation,
	pages,
	pagesMultiJump,
	previousUrl,
	textJSON,
	topNavigationVisible,
	useCustomCss,
	userNavigated,
} from './stores';
import { get, writable } from 'svelte/store';
import { updatePages } from './pages';
import textBalancer from './textBalancer';

const textBalancerClasses = [
	'.balance',
	'.textbox:not(.special_page-legal) p',
	'.textbox ul li',
	'[class*="structure"] h2',
	'.accordion > p',
	'.accordion-item-content p',
	'.accordion-item-content ul li',
	'.inputform-inputs p p',
	'p.caption',
	'.image_gallery .boxes > div p'
]

const textBalancerSelector = textBalancerClasses.join(', ');
let doBalance = true;

// try balancing all necessary elements as long as it needs and again on resize:
export function balance() {
	if(!doBalance) {
		requestAnimationFrame(balance);
		return;
	}
	textBalancer.balanceText(textBalancerSelector);
	const textBalancerElements = document.querySelectorAll(textBalancerSelector);
	doBalance = false;
	if(textBalancerElements.length < 1) {
		doBalance = true;
	}
	textBalancerElements.forEach(textBalancerElement => {
		textBalancerElement.style.maxWidth !== '' && !textBalancerElement.getAttribute('balanced') ? doBalance = true : null;
	})
	requestAnimationFrame(balance);
}

document.addEventListener('DOMContentLoaded', () => balance());
window.addEventListener('animatedPagesDone', () => doBalance = true);

let urlCache = [];

export async function fetchUrl(url) {
	
	if (urlCache[url]) {
		
		return urlCache[url];
	}
	const fetchUrl = apiUrl + url + window.location.search;
	const response = await fetch(fetchUrl);
	
	if (response.status !== 200) {
		
		throw new Error(response.status);
	}
	
	const text = await response.text();
	urlCache[url] = text;
	
	if (url === '/navigation') {
		setNavigation(text);
		updatePages(window.location.pathname + window.location.hash || '', true);
	}
	
	setRetinaImages();
	return text;
}

function setNavigation(text) {
	devideHtmlIn(text, ':scope> nav > ul >li').forEach((link_depth_0, i) => {
		navigation.update(_navigation => {
			_navigation[i] = {
				url: link_depth_0.querySelector('a').getAttribute('href'),
				text: link_depth_0.querySelector('a').innerHTML,
				children: {}
			}
			Array.from(link_depth_0.querySelectorAll('ul > li')).forEach((link_depth_1, j) => {
				_navigation[i].children[j] = {
					url: link_depth_1.querySelector('a').getAttribute('href'),
					text: link_depth_1.querySelector('a').innerHTML
					
				}
			});
			
			return _navigation;
		});
	})
}

export function getCSSvar(property, element = false) {
	return getComputedStyle(element || document.documentElement).getPropertyValue(property);
}

export function cssAnimationDuration() {
	return parseFloat(getCSSvar('--animation-duration')) * 1000;
}
export let navigatedByLink = new writable(true);
export function getLinkProps({href, isCurrent}) {
	if (isCurrent && get(previousUrl) !== href) {
		navigateThroughPages(href);
		navigatedByLink.set(true);
		return {class: 'active'};
	}
	return {rel: 'dns-prefetch'};
}

window.addEventListener('popstate', function(event) {
	navigatedByLink.set(false);
}, false);

export function navigateThroughPages(href) {
	const currentUrl = href;
	animatePages.set(true);
	if (get(topNavigationVisible)) {
		animatePages.set(false);
	}
	
	previousUrl.set(currentUrl);
	topNavigationVisible.set(false);
	userNavigated.set(true);
	pagesMultiJump.set(false);
	
	for (let i = 0; i < get(pages).length; i++) {
		if (get(pages)[i].path === currentUrl && i < get(pages).length - 2) {
			pagesMultiJump.set(true);
		}
	}
	updatePages(currentUrl, get(navigatedByLink));
	updateHistory(currentUrl);
	setRetinaImages();
	const navigatedEvent = new Event('navigated');
	window.dispatchEvent(navigatedEvent);
}

function updateHistory(currentUrl) {
	history.update((_history) => [..._history, currentUrl]);
}

export function jsToHead(href, cb) {
	if(document.head.querySelector(`script[src="${href}"]`)) {
		cb();
		return;
	}
	const link = document.createElement('script');
	document.head.appendChild(link);
	link.src = href;
	link.onload = () => cb();
};

export function cssToHead(href) {
	if(document.head.querySelector(`link[href="${href}"]`)) {
		return;
	}
	const link = document.createElement('link');
	link.type = 'text/css';
	link.rel = 'stylesheet';
	document.head.appendChild(link);
	link.href = href;
};

export function devideHtmlIn(html, selector) {
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = html;
	return tempDiv.querySelectorAll(selector);
}

export function slide(node, {
	property = 'margin-left', start = 0, end = 100, unit = '%', delay = 0, duration = cssAnimationDuration()
}) {
	return {
		delay, duration,
		
		// translation: coming in = 0 -> 1, going out = 1 -> 0
		css: transition => {
			const translate = transition * (end - start) + start;
			return `${property}: ${translate}${unit}`;
		}
	};
}

export function isValidHttpUrl(string) {
	let url;
	
	try {
		url = new URL(string);
	}
	catch (e) {
		return false;
	}
	
	return url.protocol === "http:" || url.protocol === "https:";
}

export function isCustomCssActive() {
	if (!get(cookiesActivated)) {
		return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
	}
	return JSON.parse(localStorage.getItem('b1-cookies'))['useCustomCss'] === true;
}

export function writeLocalStorage(settings) {
	localStorage.setItem('b1-cookies', JSON.stringify({
		...JSON.parse(localStorage.getItem('b1-cookies')),
		...settings
	}));
}

export function isRetinaDisplay() {
	if (window.matchMedia) {
		const mq = window.matchMedia("only screen and (min--moz-device-pixel-ratio: 1.3), only screen and" +
			" (-o-min-device-pixel-ratio: 2.6/2), only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen  and (min-device-pixel-ratio: 1.3), only screen and (min-resolution: 1.3dppx)");
		return ((mq && mq.matches) || (window.devicePixelRatio > 1.3));
	}
}

export function setRetinaImages() {
	if(!isRetinaDisplay()) {
		return;
	}
	const images = document.querySelectorAll('img:not([src*="_RETINA"]):not([retinaLoaded])');
	if(images.length < 1) {
		const imagesLoadedEvent = new Event('imagesLoaded');
		window.dispatchEvent(imagesLoadedEvent);
		return;
	}
	images.forEach(image => {
		
		/**
		 * Replaces the file extension of an image URL with "_RETINA" and keeps the original extension.
		 *
		 * @param {string} imageUrl - The URL of the image with a file extension.
		 * @returns {string} The modified image URL with "_RETINA" appended before the original extension.
		 */
		
		/**
		 * /\.: The backslash escapes the dot (".") character, indicating a literal dot.
		 * (webp): The parentheses denote a capturing group. It captures the string "webp", which represents the file
		 *  extension to be replaced.
		 
		 * '_RETINA.': The string "_RETINA." is a fixed part of the replacement. It will be added before the captured
		 *  extension.
		 * $1: This is a backreference that refers to the first capturing group in the pattern. In this case, it
		 *  references the captured extension "(webp)".
		 */
		const retinaUrl = image.src.replace(/\.(webp)/, '_RETINA.$1');
		
		if (!retinaUrl.indexOf('_RETINA')) {
			return;
		}
		const testImg = new Image();
		
		testImg.onload = () => {
			
			if (testImg.complete) {
				image.loading = 'lazy';
				image.srcset = image.src + ' 1x';
			}
		};
		testImg.src = image.src;
		const retinaImg = new Image();
		
		retinaImg.onload = () => {
			if (testImg.complete) {
				image.srcset = image.src + ' 1x,' + retinaUrl + ' 2x';
			}
		};
		retinaImg.src = retinaUrl;
		image.setAttribute('retinaLoaded', true);
	});
}

let anchorElement = false;

export function jumpToAnchor(anchor) {
	if(!anchor || anchor === '') {
		anchorElement = false;
		return;
	}
	window.location.hash = anchor;
}

function jumpByAnchorListener() {
	if(!anchorElement) {
		return;
	}
	if(anchorElement.getBoundingClientRect().top > window.innerHeight) {
		requestAnimationFrame(jumpByAnchorListener);
	}
	anchorElement.scrollIntoView({ behavior: 'instant' });
	const rightScrollBox = document.querySelector('.content .container > .right');
	rightScrollBox.scrollBy({top: -50});
}

export function jumpByAnchor(elementWithId) {
	const anchor = window.location.href.split('#')[1] || false;
	if(!anchor) {
		return;
	}
	anchorElement = document.querySelector(`.content .container .right #${anchor}`)
		|| document.querySelector(`.content .container .left #${anchor}`)
		|| elementWithId;
	if(anchor && anchor !== '' && anchorElement.id === anchor) {
		window.removeEventListener('imagesLoaded', jumpByAnchorListener);
		window.addEventListener('imagesLoaded', jumpByAnchorListener);
	}
}

function setAdMaxScale() {
	const commercials = document.querySelectorAll('.commercial');
	commercials.forEach(commercial => {
		commercial.style.setProperty('--maxScale', (commercial.getBoundingClientRect().width + 20) / commercial.getBoundingClientRect().width);
		if(commercial.getBoundingClientRect().height > commercial.getBoundingClientRect().width) {
			commercial.style.setProperty('--maxScale', (commercial.getBoundingClientRect().height + 20) / commercial.getBoundingClientRect().height);
		}
	})
	requestAnimationFrame(setAdMaxScale);
}
setAdMaxScale();

function addElementReferencesInPages(_pages, container, scrollTop) {
	function addRefs(elements) {
		return Array.from(elements).map(element => {
			const elementRect = element.getBoundingClientRect();
			const parentRect = element.parentElement.getBoundingClientRect();
			// ref could be src, id, or classNames as concatenated string
			const ref = element?.src ||
				element.id ||
				Array.from(element.childNodes).map(node => node.className).reduce((sum, a) => sum += a != ''? a : '', '');
			return {
				ref,
				parentAspectRatio: parentRect.height ? parentRect.width / parentRect.height : 'auto',
				parentHeight: parentRect.height,
				aspectRatio: elementRect.height ? elementRect.width / elementRect.height : 'auto',
				containerWidth: container.getBoundingClientRect().width
			};
		});
	}
	// only users manual scrolling can change this:
	if(!_pages[_pages.length - 1].autoScroll) {
		_pages[_pages.length - 1].scrollTop = scrollTop;
	}
	_pages[_pages.length - 1].refs = [
		// remember "top-level" divs:
		...addRefs(container.querySelector('.inner > ul > li').children),
		// remember images:
		...addRefs(container.querySelectorAll('img'))
	];
	return _pages;
}

function rightScrollListener() {
	const rightContainer = document.querySelector('.content .container > .right .outer');
	const scrollTop = document.querySelector('.content .container > .right').scrollTop;
	if(rightContainer.getBoundingClientRect().top !== 0 && scrollTop !== 0) {
		pages.update(_pages => {
			_pages = addElementReferencesInPages(_pages, rightContainer, scrollTop);
			return _pages;
		})
	}
}

function startRightScrollListener() {
	if(!document.querySelector('.right')) {
		requestAnimationFrame(startRightScrollListener);
		return;
	}
	const rightScrollBox = document.querySelector('.content .container > .right');
	rightScrollBox.removeEventListener('scroll', rightScrollListener);
	rightScrollBox.addEventListener('scroll', rightScrollListener);
	eagerLoadingLeftImages();
}

// avoid effect of "scrolling to wrong position" on left-hand Page due to lazy image loading
export function eagerLoadingLeftImages() {
	if(get(pages).length < 2) {
		return;
	}
	const leftScrollBox = document.querySelector('.content .container > .left');
	if(leftScrollBox && leftScrollBox.querySelector('img')) {
		const leftImages = leftScrollBox.querySelectorAll('img');
		Array.from(leftImages).forEach(img => img.setAttribute('loading', 'eager'));
	} else {
		requestAnimationFrame(eagerLoadingLeftImages);
	}
}

// promptly loading images - avoids effect of "scrolling to wrong position" on left-hand Page due to lazy image loading
export function eagerLoadingRightImages() {
	if(get(pages).length < 2) {
		return;
	}
	const rightScrollBox = document.querySelector('.content .container > .right');
	if(rightScrollBox && rightScrollBox.querySelector('img')) {
		const rightImages = rightScrollBox.querySelectorAll('img');
		Array.from(rightImages).forEach(img => img.setAttribute('loading', 'eager'));
	}
}

window.addEventListener('animatedPagesDone', () => {
	startRightScrollListener();
	eagerLoadingLeftImages();
	eagerLoadingRightImages();
});

export function chromeFixImageFlex() {
	const rightScrollBox = document.querySelector('.content .container > .right');
	if(rightScrollBox && rightScrollBox.querySelector('img')) {
		const rightImages = rightScrollBox.querySelectorAll('img');
		Array.from(rightImages).forEach(img => {
			if(img.getAttribute('chromeFixImageFlex')) {
				return;
			}
			img.setAttribute('chromeFixImageFlex', true);
			img.addEventListener('load', () => {
				if(!img) {
					return;
				}
				if(img.parentElement?.children.length === 1 || img.parentElement?.classList.contains('container-overlay')) {
					img.parentElement.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
				}
			})
		});
	}
	requestAnimationFrame(chromeFixImageFlex);
}

window.addEventListener('animatedPagesDone', () => chromeFixImageFlex());
window.addEventListener('load', () => chromeFixImageFlex());

export function fixImageAndApiLinkPath(html) {
	let tempDiv = document.createElement('div');
	tempDiv.innerHTML = html || this;
	Array.from(tempDiv.querySelectorAll('img')).forEach(img => {
		if (!isValidHttpUrl(img.getAttribute('src'))) {
			img.setAttribute('src', apiUrl + img.getAttribute('src'));
		}
	})
	Array.from(tempDiv.querySelectorAll('a[href*=".vcf"]')).forEach(vcf => {
		vcf.setAttribute('href', apiUrl + vcf.getAttribute('href'));
	})
	Array.from(tempDiv.querySelectorAll('*[style*="url("]')).forEach(backgroundImg => {
		const fixedImagePath = backgroundImg.getAttribute('style').replace(/url\(([^)]*)\)/g, (match, contents) => {
			if (!isValidHttpUrl(contents)) {
				return `url(${apiUrl}${contents})`;
			}
			return contents;
		});
		backgroundImg.setAttribute('style', fixedImagePath);
	})
	setRetinaImages();
	return tempDiv.innerHTML;
}
String.prototype.fixImageAndApiLinkPath = fixImageAndApiLinkPath;

function readTextJson() {
	fetch(apiUrl + '/specialcomponenttexts')
		.then((response) => response.json())
		.then((json) => textJSON.set(json));
}

readTextJson();

export function getText(path, children = false) {
	path = (typeof path).toLowerCase() === 'string' ? path.split('.') : path;
	if(path.length > 1) {
		return getText([...path.slice(1)], (children || get(textJSON))[path[0]]);
	}
	return (children || get(textJSON))[path[0]];
}

// store and get "random" content in page:
export function setPagesFixedRandomContent(index, content) {
	pages.update(_pages => {
		if(!_pages[_pages.length - 1].randomContent) {
			_pages[_pages.length - 1].randomContent = [];
		}
		_pages[_pages.length - 1].randomContent[index] = content;
		return _pages;
	});
}
export function getPagesFixedRandomContent(isRightSide, index) {
	const _pages = get(pages);
	return _pages[_pages.length - (isRightSide? 1 : 2)]?.randomContent?.[index] || false;
}
